import Marker from '../Marker';
import subpixel from '../util/canvasSubpixelOffset';

/** Line marker */
class LineMarker extends Marker {
	getDefaultParams() {
		return {
			lineWidth: 1,
			lineColor: '#000',
			fillColor: 'none',
			fillBetweenColor: '#aaa'
		};
	}

	render(
		ctx,
		data,
		xScale,
		yScale,
		color,
		lineWidth,
		lineDash,
		isStepped
	) {
		if (!data.length || !data[0].length) return;

		if (ctx.setLineDash && lineDash) {
			ctx.setLineDash(lineDash.split(/\s*,\s*/));
		}

		ctx.lineJoin = 'round';

		// lineWidth defaults
		if (lineWidth === null) lineWidth = this.params.lineWidth;

		// don't render if lineWidth is 0
		if (lineWidth === 0) {
			// reset dashed line
			if (ctx.setLineDash) ctx.setLineDash([]);
			return;
		}

		ctx.lineWidth = lineWidth;
		ctx.strokeStyle = color || this.params.lineColor;

		ctx.beginPath();

		ctx.moveTo(subpixel.px(xScale(data[0][0])), subpixel.px(yScale(data[0][1])));

		this.getLine(ctx, data, xScale, yScale, false, isStepped);

		ctx.stroke();

		// reset dashed line
		if (ctx.setLineDash) ctx.setLineDash([]);
	}

	getCoords(ctx, data, xScale, yScale) {
		if (!data.length || !data[0].length) return;

		const coords = [];

		coords.push([subpixel.px(xScale(data[0][0])), subpixel.px(yScale(data[0][1]))]);

		return [...coords, ...this.getLine(ctx, data, xScale, yScale, true, true)];
	}

	renderFill(ctx, data, height, xScale, yScale, fillStyle, fillToValue) {
		if (!data.length || data.length < 2 || !data[0].length) return;

		ctx.lineJoin = 'round';
		ctx.fillStyle = fillStyle || this.params.fillColor;
		ctx.beginPath();

		// get vertices for normal line
		const firstIndex = this.getLine(ctx, data, xScale, yScale);
		let y0;

		// complete the fill region with two extra vertices
		if (typeof fillToValue === 'number') {
			// fill to a specific value (commonly 0.0)
			y0 = subpixel.px(Math.min(yScale(fillToValue), height));
		} else {
			// fill to bottom of panel
			y0 = subpixel.px(height - 1);
		}

		// add a vertex at (last index, y0)
		if (data.length > 0) {
			ctx.lineTo(subpixel.px(xScale(data[data.length - 1][0])), y0);
		}

		// add a vertex at (first index, y0)
		if (
			typeof firstIndex === 'number' &&
			typeof data[firstIndex] !== 'undefined'
		) {
			ctx.lineTo(subpixel.px(xScale(data[firstIndex][0])), y0);
		}

		ctx.fill();
	}
	renderFillBetween(ctx, dataUp, dataDown, xScale, yScale, fillStyle) {
		if (!dataUp.length || !dataUp[0].length) return;

		ctx.lineJoin = 'round';
		ctx.fillStyle = fillStyle || this.params.fillBetweenColor;
		ctx.beginPath();

		ctx.moveTo(
			subpixel.px(xScale(dataUp[0][0])),
			subpixel.px(yScale(dataUp[0][1]))
		);

		this.getLine(ctx, dataUp, xScale, yScale);

		ctx.lineTo(
			subpixel.px(xScale(dataDown[dataDown.length - 1][0])),
			subpixel.px(yScale(dataUp[dataUp.length - 1][1]))
		);
		ctx.lineTo(
			subpixel.px(xScale(dataDown[dataDown.length - 1][0])),
			subpixel.px(yScale(dataDown[dataDown.length - 1][1]))
		);

		this.getLine(
			ctx,
			Array.prototype.slice.call(dataDown).reverse(),
			xScale,
			yScale
		);

		ctx.lineTo(
			subpixel.px(xScale(dataDown[0][0])),
			subpixel.px(yScale(dataDown[0][1]))
		);

		ctx.fill();
	}
	getLine(ctx, data, xScale, yScale, getData, isStepped) {
		let x0 = 0;
		let y0 = 0;
		let xLast = 0;
		let yLast = 0;
		let firstIndex;
		const coords = [];

		for (let x = 0, xLen = data.length; x < xLen; x++) {
			x0 = subpixel.px(xScale(data[x][0]));
			y0 = subpixel.px(yScale(data[x][1]));

			if (!isNaN(y0) && !isNaN(x0)) {
				if (typeof firstIndex !== 'number') {
					firstIndex = x;
				}

				if (x0 !== xLast || y0 !== yLast) {
					if (yLast && isStepped) {
						coords.push([x0, yLast]);
					}

					coords.push([x0, y0]);
				}

				xLast = x0;
				yLast = y0;
			}
		}

		// draw coords collection
		for (let x = 0, xLen = coords.length; x < xLen; x++) {
			ctx.lineTo(coords[x][0], coords[x][1]);
		}

		if (getData) {
			return coords;
		}
		return firstIndex;
	}

	// this is mostly like getLine(), but it just returns an array of coordinates for the isWithin check
	getLineCoords(data, xScale, yScale, isStepped) {
		if (!data || !xScale || !yScale) return [];
		let x0 = 0;
		let y0 = 0;
		let xLast = 0;
		let yLast = 0;
		const coords = [];

		for (let x = 0, xLen = data.length; x < xLen; x++) {
			x0 = xScale(data[x][0]);
			y0 = yScale(data[x][1]);

			if (!isNaN(y0) && !isNaN(x0)) {
				if (x0 !== xLast || y0 !== yLast) {
					if (yLast && isStepped) {
						coords.push([x0, yLast]);
					}

					coords.push([x0, y0]);
				}

				xLast = x0;
				yLast = y0;
			}
		}

		return coords;
	}
}

export default LineMarker;
